import React, { useState } from 'react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Search, FileSpreadsheet, FileText, Package, ArrowUpDown, AlertTriangle, Info } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { MaterialSummaryItem } from '@/hooks/useMaterialsSummary';
import { exportMaterialsSummaryToExcel } from '@/utils/export/exportMaterialsSummaryExcel';
import { exportMaterialsSummaryToPDF } from '@/utils/export/exportMaterialsSummaryPDF';
import { exportCompleteRDA } from '@/utils/export/exportCompleteRDA';
import { toast } from 'sonner';
import { formatNumber } from '@/utils/format';

interface MaterialsSummaryTableProps {
  materials: MaterialSummaryItem[];
  totalCost: number;
  totalLaborCost: number;
  totalByCategory: Record<string, number>;
  estimateName?: string;
  estimate?: any;
  stratigraphies?: any[];
}

const MaterialsSummaryTable = ({ 
  materials, 
  totalCost, 
  totalLaborCost, 
  totalByCategory,
  estimateName = 'Preventivo',
  estimate,
  stratigraphies = []
}: MaterialsSummaryTableProps) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [sortField, setSortField] = useState<keyof MaterialSummaryItem>('category');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  // Filtri
  const filteredMaterials = materials.filter(material => {
    const matchesSearch = material.materialName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         material.materialCode.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         material.supplier.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = categoryFilter === 'all' || material.category === categoryFilter;
    return matchesSearch && matchesCategory;
  });

  // Ordinamento
  const sortedMaterials = [...filteredMaterials].sort((a, b) => {
    const aValue = a[sortField];
    const bValue = b[sortField];
    
    if (typeof aValue === 'string' && typeof bValue === 'string') {
      return sortDirection === 'asc' 
        ? aValue.localeCompare(bValue)
        : bValue.localeCompare(aValue);
    }
    
    if (typeof aValue === 'number' && typeof bValue === 'number') {
      return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
    }
    
    return 0;
  });

  const categories = [...new Set(materials.map(m => m.category))].sort();

  const handleSort = (field: keyof MaterialSummaryItem) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Calcolo totale preventivo
  const totalEstimate = totalCost + totalLaborCost;

  const handleExportExcel = () => {
    try {
      exportMaterialsSummaryToExcel(materials, estimateName, totalCost, totalLaborCost);
      toast.success('Export Excel completato', {
        description: 'Il file Excel è stato scaricato con successo.'
      });
    } catch (error) {
      console.error('Errore durante export Excel:', error);
      toast.error('Errore durante export Excel', {
        description: 'Si è verificato un errore durante la generazione del file.'
      });
    }
  };

  const handleExportPDF = () => {
    try {
      exportMaterialsSummaryToPDF(materials, estimateName, totalCost, totalLaborCost);
      toast.success('Export PDF completato', {
        description: 'Il file PDF è stato scaricato con successo.'
      });
    } catch (error) {
      console.error('Errore durante export PDF:', error);
      toast.error('Errore durante export PDF', {
        description: 'Si è verificato un errore durante la generazione del file.'
      });
    }
  };

  const handleExportCompleteRDA = async () => {
    if (!estimate || !stratigraphies.length) {
      toast.error('Dati mancanti', {
        description: 'Impossibile generare RDA Completa: mancano i dati del preventivo o delle stratigrafie.'
      });
      return;
    }

    try {
      await exportCompleteRDA(estimate, stratigraphies);
      toast.success('RDA Completa generata con successo!', {
        description: 'Il documento completo con tutte le stratigrafie è stato scaricato.'
      });
    } catch (error) {
      console.error('Errore export RDA Completa:', error);
      toast.error('Errore durante la generazione RDA Completa', {
        description: 'Si è verificato un errore durante la generazione del documento.'
      });
    }
  };

  return (
    <div className="space-y-4">
      {/* Filtri e Ricerca */}
      <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
        <div className="flex flex-col sm:flex-row gap-2 flex-1">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
            <Input
              placeholder="Cerca materiali..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
          <Select value={categoryFilter} onValueChange={setCategoryFilter}>
            <SelectTrigger className="w-48">
              <SelectValue placeholder="Filtra per categoria" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutte le categorie</SelectItem>
              {categories.map(category => (
                <SelectItem key={category} value={category}>
                  {category} (€{formatNumber(totalByCategory[category] ?? 0)})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" className="gap-2" onClick={handleExportExcel}>
            <FileSpreadsheet className="h-4 w-4" />
            Esporta Dati in Excel
          </Button>
          <Button variant="outline" className="gap-2" onClick={handleExportPDF}>
            <FileText className="h-4 w-4" />
            Stampa RDA
          </Button>
          <Button variant="outline" className="gap-2" onClick={handleExportCompleteRDA}>
            <FileText className="h-4 w-4" />
            Stampa RDA Completa
          </Button>
        </div>
      </div>

      {/* Statistiche Rapide */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-card p-4 rounded-lg border">
          <div className="flex items-center gap-2">
            <Package className="h-4 w-4 text-primary" />
            <span className="text-sm font-medium">Totale Materiali</span>
          </div>
          <p className="text-2xl font-bold">{materials.length}</p>
        </div>
        <div className="bg-card p-4 rounded-lg border">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium">Costo Totale</span>
          </div>
          <p className="text-2xl font-bold text-primary">€{formatNumber(totalCost)}</p>
        </div>
        <div className="bg-card p-4 rounded-lg border">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium">Fornitori</span>
          </div>
          <p className="text-2xl font-bold">{new Set(materials.map(m => m.supplier)).size}</p>
        </div>
        <div className="bg-card p-4 rounded-lg border">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium">Categorie</span>
          </div>
          <p className="text-2xl font-bold">{categories.length}</p>
        </div>
      </div>

      {/* Banner informativo: quantità d'acquisto vs preventivo cliente */}
      <Alert className="bg-blue-50 border-blue-200">
        <Info className="h-4 w-4 text-blue-700" />
        <AlertDescription className="text-xs text-blue-900">
          Questo riepilogo mostra le <strong>quantità da ordinare al fornitore</strong> (RDA),
          con sfridi e arrotondamenti ai pezzi/scatole interi.
          <strong> Non sono le quantità del preventivo cliente</strong>: il cliente paga la superficie/lunghezza reale,
          tu acquisti un po' di più per coprire scarti di taglio. Sfrido configurabile per categoria in
          <strong> Impostazioni → Sfridi</strong>, o override sul singolo materiale.
        </AlertDescription>
      </Alert>

      {/* Tabella */}
      <div className="border rounded-lg">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>
                <Button variant="ghost" onClick={() => handleSort('category')} className="h-8 p-2">
                  Categoria
                  <ArrowUpDown className="ml-2 h-3 w-3" />
                </Button>
              </TableHead>
              <TableHead>
                <Button variant="ghost" onClick={() => handleSort('materialName')} className="h-8 p-2">
                  Materiale
                  <ArrowUpDown className="ml-2 h-3 w-3" />
                </Button>
              </TableHead>
              <TableHead>
                <Button variant="ghost" onClick={() => handleSort('supplier')} className="h-8 p-2">
                  Fornitore
                  <ArrowUpDown className="ml-2 h-3 w-3" />
                </Button>
              </TableHead>
              <TableHead className="text-right">
                <TooltipProvider delayDuration={200}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button variant="ghost" onClick={() => handleSort('totalQuantity')} className="h-8 p-2">
                        Quantità d'acquisto
                        <Info className="ml-1.5 h-3 w-3 opacity-60" />
                        <ArrowUpDown className="ml-1.5 h-3 w-3" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent side="top" className="max-w-[280px]">
                      <p className="text-xs">
                        Quantità da ordinare al fornitore, già <strong>maggiorata dello sfrido</strong> e arrotondata
                        a pezzi/scatole interi. Sotto la quantità trovi il dato teorico + % sfrido applicato.
                      </p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </TableHead>
              <TableHead className="text-right">
                <Button variant="ghost" onClick={() => handleSort('unitPrice')} className="h-8 p-2">
                  Prezzo Unit.
                  <ArrowUpDown className="ml-2 h-3 w-3" />
                </Button>
              </TableHead>
              <TableHead className="text-right">
                <Button variant="ghost" onClick={() => handleSort('totalCost')} className="h-8 p-2">
                  Totale
                  <ArrowUpDown className="ml-2 h-3 w-3" />
                </Button>
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {sortedMaterials.map((material, index) => (
              <TableRow key={`${material.materialId}-${index}`}>
                <TableCell>
                  <Badge variant="outline">{material.category}</Badge>
                </TableCell>
                <TableCell>
                  <div>
                    <div className="font-medium flex items-center gap-1.5 flex-wrap">
                      <span>{material.materialName}</span>
                      {/* F32: warning giunto orizzontale necessario. */}
                      {material.hasHorizontalJoint && (
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Badge
                                variant="outline"
                                className="text-[10px] gap-1 bg-amber-50 text-amber-800 border-amber-300 cursor-help"
                              >
                                <AlertTriangle className="h-3 w-3" />
                                Giunto orizzontale
                              </Badge>
                            </TooltipTrigger>
                            <TooltipContent className="max-w-xs">
                              Il foglio scelto è più corto dell'altezza parete: il posatore
                              dovrà fare almeno una giunzione orizzontale. Se possibile,
                              scegli un formato più alto nel catalogo per evitare il giunto.
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      )}
                    </div>
                    <div className="text-sm text-muted-foreground">
                      {material.materialCode && `Cod: ${material.materialCode}`}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      Usato in: {material.stratigraphyNames.join(', ')}
                    </div>
                  </div>
                </TableCell>
                <TableCell>
                  <span className="text-sm">{material.supplier}</span>
                </TableCell>
                <TableCell className="text-right">
                  <div className="font-medium">
                    {formatNumber(material.totalQuantity)} {material.unit}
                  </div>
                  {/* Per le viti: pezzi usati + scatole + sfrido */}
                  {material.piecesUsed !== undefined && material.boxPieces && (
                    <div className="text-[10px] text-muted-foreground leading-tight mt-0.5">
                      {Math.round(material.piecesUsed)} pz utilizzati
                      {material.wastePercentage && material.wastePercentage > 0
                        ? ` · +${material.wastePercentage}% sfrido`
                        : ''}
                      {` · ${material.boxPieces} pz/scatola`}
                    </div>
                  )}
                  {/* Altri materiali: quantità teorica + sfrido (se > 0) */}
                  {material.piecesUsed === undefined
                    && material.wastePercentage !== undefined
                    && material.wastePercentage > 0
                    && material.theoreticalQuantity !== undefined && (
                    <div className="text-[10px] text-muted-foreground leading-tight mt-0.5">
                      {formatNumber(material.theoreticalQuantity)} {material.unit} teorici
                      {` · +${material.wastePercentage}% sfrido`}
                    </div>
                  )}
                </TableCell>
                <TableCell className="text-right">
                  <span className="text-sm">
                    €{formatNumber(material.unitPrice, material.unitPrice < 0.1 ? 4 : 2)}/{material.unit}
                  </span>
                </TableCell>
                <TableCell className="text-right">
                  <span className="font-medium">
                    €{formatNumber(material.totalCost)}
                  </span>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Totali - Tre box affiancati */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="text-center">
            <div className="text-sm text-muted-foreground">Costo Totale Materiali</div>
            <div className="text-2xl font-bold text-blue-700">€{formatNumber(totalCost)}</div>
          </div>
        </div>
        
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="text-center">
            <div className="text-sm text-muted-foreground">Costo Totale Manodopera</div>
            <div className="text-2xl font-bold text-green-700">€{formatNumber(totalLaborCost)}</div>
          </div>
        </div>
        
        <div className="bg-primary/5 border border-primary/20 rounded-lg p-4">
          <div className="text-center">
            <div className="text-sm text-muted-foreground">Totale Preventivo</div>
            <div className="text-2xl font-bold text-primary">€{formatNumber(totalEstimate)}</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MaterialsSummaryTable;
